import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Policy } from '../interfaces/policy.interface';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class PoliciesService {
  constructor(
    private firestore: AngularFirestore,
    private auth: AuthService
  ) {}

  // Get all Policies (for admins)
  getAllPolicies(): Observable<Policy[]> {
    return this.firestore.collection<Policy>('policies')
      .snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as Omit<Policy, 'id'>;
          const id = a.payload.doc.id;
          return { id, ...data };
        }))
      );
  }

  // Get published Policies only (for regular users)
  getPublishedPolicies(): Observable<Policy[]> {
    return this.firestore.collection<Policy>('policies', ref => 
      ref.where('status', '==', 'published')
    )
    .snapshotChanges()
    .pipe(
      map(actions => actions.map(a => {
        const data = a.payload.doc.data() as Omit<Policy, 'id'>;
        const id = a.payload.doc.id;
        return { id, ...data };
      }))
    );
  }

  // Get drafts (for admins only)
  getDrafts(): Observable<Policy[]> {
    return this.firestore.collection<Policy>('policies', ref => 
      ref.where('status', '==', 'draft')
    )
    .snapshotChanges()
    .pipe(
      map(actions => actions.map(a => {
        const data = a.payload.doc.data() as Omit<Policy, 'id'>;
        const id = a.payload.doc.id;
        return { id, ...data };
      }))
    );
  }

  // Create new Policy
  async createPolicy(policy: Partial<Policy>): Promise<string> {
    const policyData = {
      ...policy,
      content: policy.content?.replace(/\n/g, '\n'),
      updatedAt: new Date()
    };
    return this.firestore.collection('policies').add(policyData)
      .then(ref => ref.id);
  }

  // Update existing Policy
  async updatePolicy(id: string, policy: Partial<Policy>): Promise<void> {
    const policyData = {
      ...policy,
      content: policy.content?.replace(/\n/g, '\n'),
      updatedAt: new Date()
    };
    return this.firestore.doc(`policies/${id}`).update(policyData);
  }

  // Get Policy by slug
  getPolicyBySlug(slug: string): Observable<Policy | null> {
    return this.auth.user$.pipe(
      switchMap(user => {
        if (!user) {
          console.log('No user logged in');
          return of(null);
        }
        
        return this.firestore
          .collection<Policy>('policies', ref => 
            ref.where('slug', '==', slug)
            .limit(1)
          )
          .snapshotChanges()
          .pipe(
            map(actions => {
              if (actions.length === 0) return null;
              const doc = actions[0];
              const data = doc.payload.doc.data() as Omit<Policy, 'id'>;
              return { id: doc.payload.doc.id, ...data };
            })
          );
      })
    );
  }

  // Get Policy by ID
  getPolicyById(id: string): Observable<Policy | null> {
    return this.firestore
      .collection<Policy>('policies')
      .doc(id)
      .snapshotChanges()
      .pipe(
        map(doc => {
          if (!doc.payload.exists) return null;
          const data = doc.payload.data() as Omit<Policy, 'id'>;
          return { id: doc.payload.id, ...data };
        })
      );
  }

  // Delete Policy
  async deletePolicy(id: string): Promise<void> {
    try {
      await this.firestore.collection('policies').doc(id).delete();
    } catch (error) {
      console.error('Error deleting Policy:', error);
      throw error;
    }
  }

  // Get Policies with auth check
  getPolicies(): Observable<Policy[]> {
    return this.auth.user$.pipe(
      switchMap(user => {
        if (!user) {
          console.log('No user logged in');
          return of([]);
        }
        
        return this.auth.isAdmin().pipe(
          switchMap(isAdmin => {
            console.log('Is admin:', isAdmin);
            
            if (isAdmin) {
              // Admins can see all Policies
              return this.firestore.collection<Policy>('policies', ref => 
                ref.orderBy('updatedAt', 'desc')
              )
              .snapshotChanges()
              .pipe(
                map(actions => actions.map(a => {
                  const data = a.payload.doc.data() as Omit<Policy, 'id'>;
                  const id = a.payload.doc.id;
                  return { id, ...data };
                }))
              );
            } else {
              // Regular users only see published Policies
              return this.firestore.collection<Policy>('policies', ref => 
                ref.where('status', '==', 'published')
                .orderBy('updatedAt', 'desc')
              )
              .snapshotChanges()
              .pipe(
                map(actions => actions.map(a => {
                  const data = a.payload.doc.data() as Omit<Policy, 'id'>;
                  const id = a.payload.doc.id;
                  return { id, ...data };
                }))
              );
            }
          })
        );
      })
    );
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  }
} 